import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthSharedService } from '@core-lib/services/auth.service';
import { environment } from '@environments/environment';
import { BehaviorSubject, first, map, Observable, combineLatest } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
	Cheque,
	IEditPreferences,
	IPreference,
	PreferenceContact,
	PreferenceModeReception,
	PreferencesNotifications,
	ReleveAffaire
} from '@core-lib/models/preference.model';

@Injectable({
	providedIn: 'root'
})
export class PreferenceService {
	get tenantKey() {
		return this.autService.currentTenantKey;
	}

	preferences$: BehaviorSubject<IPreference[]> = new BehaviorSubject<IPreference[]>([]);

	get preferencesNotifications$(): Observable<PreferencesNotifications> {
		return this.preferences$.pipe(map(preferences => new PreferencesNotifications(preferences)));
	}

	get preferencesModeReception$(): Observable<PreferenceModeReception> {
		return this.preferences$.pipe(map(preferences => new PreferenceModeReception(preferences)));
	}

	get preferencesReleveAffaires$(): Observable<ReleveAffaire> {
		return this.preferences$.pipe(map(preferences => new ReleveAffaire(preferences)));
	}

	get preferencesCheques$(): Observable<Cheque> {
		return this.preferences$.pipe(map(preferences => new Cheque(preferences)));
	}

	get preferencesContact$(): Observable<PreferenceContact> {
		return this.preferences$.pipe(map(preferences => new PreferenceContact(preferences)));
	}

	editLoading$ = new BehaviorSubject<boolean>(false);
	getLoading$ = new BehaviorSubject<boolean>(false);

	private loadedKey: string | null = null;
	private inFlightKey: string | null = null;

	contactEditMode$ = new BehaviorSubject<boolean>(false);
	chequeEditMode$ = new BehaviorSubject<boolean>(false);
	modeReceptionEditMode$ = new BehaviorSubject<boolean>(false);
	notificationsEditMode$ = new BehaviorSubject<boolean>(false);
	releveAffaireEditMode$ = new BehaviorSubject<boolean>(false);

	get anyEditMode$() {
		return combineLatest([
			this.chequeEditMode$,
			this.contactEditMode$,
			this.modeReceptionEditMode$,
			this.notificationsEditMode$,
			this.releveAffaireEditMode$
		]).pipe(
			map(([cheque, contact, modeRception, notif, releveAffaire]) => cheque || contact || modeRception || notif || releveAffaire)
		);
	}

	get getLoading() {
		return this.getLoading$.value;
	}

	constructor(
		private http: HttpClient,
		private autService: AuthSharedService
	) {}

	get(structureId: string, force = false): void {
		// Evite les appels redondants : meme tenant + structureId deja charge ou deja en vol.
		// La cle inclut le tenant car les preferences sont scopees par tenant (cf. URL).
		// force = true pour rafraichir apres une edition.
		const key = `${this.tenantKey}/${structureId}`;
		if (!force && (this.inFlightKey === key || this.loadedKey === key)) {
			return;
		}
		this.inFlightKey = key;
		this.getLoading$.next(true);
		this.http
			.get<IPreference[]>(`${environment.apiUrl}/t/${this.tenantKey}/preference/${structureId}`)
			.pipe(
				first(),
				finalize(() => {
					if (this.inFlightKey === key) {
						this.inFlightKey = null;
					}
					this.getLoading$.next(false);
				})
			)
			.subscribe({
				next: preferences => {
					this.preferences$.next(preferences);
					this.loadedKey = key;
				},
				error: () => {}
			});
	}

	edit(structureId: string, request: IEditPreferences): Observable<any> {
		this.editLoading$.next(true);
		return this.http
			.post(`${environment.apiUrl}/t/${this.tenantKey}/preference/${structureId}/${request.category}`, request.preferences)
			.pipe(
				first(),
				finalize(() => this.editLoading$.next(false))
			);
	}
}
